import React, { useState, useRef, useEffect, useCallback } from 'react'; 
import './LSpacyHub.css'; 

// --- 🩹 CIRUGÍA DE ASSETS: Nombres de archivo corregidos (sin espacios) ---
// ── POR QUE VIDEO Y NO GIF ──────────────────────────────────────────────────
// Los gifs de Spacy son de 1080x1080 y pesan 44 MB entre todos; el de hablar,
// el solo, 26,5 MB. Medido en esta misma pagina: un visitante que entraba desde
// el movil se descargaba 45 MB antes de ver a Spacy moverse. Eso no es un
// problema de rendimiento, es la primera impresion de NSL.
//
// Los mismos videos a 512 px pesan 636 KB entre todos —72 veces menos— y a
// tamano de pantalla se ven igual. Un gif no se puede comprimir de verdad
// porque no comprime entre fotogramas: guarda cada cuadro casi entero.
//
// Los .gif siguen en assets/ por si hiciera falta volver.
import SpacyNormal from '../../assets/spacy-normal.mp4';
import SpacyHablando from '../../assets/spacy-hablando.mp4';
import SpacyPensando from '../../assets/spacy-pensando.mp4';
import SpacyEscuchando from '../../assets/spacy-escuchando.mp4';
import SpacyError from '../../assets/spacy-error.mp4';
import { duracionDeLaPrueba } from "../../limites";

// (Los hooks useBrowserTTS y useBrowserSTT se mantienen 100% idénticos)
// ... (useBrowserTTS) ...
const useBrowserTTS = () => {
  const [speaking, setSpeaking] = useState(false);
  const [spanishVoice, setSpanishVoice] = useState(null);

  useEffect(() => {
    const getVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      const voice = 
        voices.find((v) => v.lang === 'es-MX') ||
        voices.find((v) => v.lang === 'es-ES') ||
        voices.find((v) => v.lang.startsWith('es'));
      
      setSpanishVoice(voice || voices[0]);
    };
    getVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = getVoices;
    }
  }, []);

  const speak = useCallback(({ text }) => {
    if (!text || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    if (spanishVoice) {
      utterance.voice = spanishVoice;
      utterance.lang = spanishVoice.lang;
    } else {
      utterance.lang = 'es';
    }
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
  }, [spanishVoice]); 

  const cancel = useCallback(() => {
    if (window.speechSynthesis) {
      setSpeaking(false);
      window.speechSynthesis.cancel();
    }
  }, []); 

  return { speak, cancel, speaking };
};
// ... (useBrowserSTT) ...
const useBrowserSTT = ({ lang = 'es-MX' } = {}) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error("Este navegador no soporta Speech Recognition.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.continuous = false; 
    recognition.interimResults = false; 

    recognition.onstart = () => {
      setIsListening(true);
      setTranscript(''); 
    };
    recognition.onend = () => {
      setIsListening(false);
    };
    recognition.onerror = (event) => {
      console.error("Error en Speech Recognition:", event.error);
      setIsListening(false);
    };
    recognition.onresult = (event) => {
      const newTranscript = event.results[event.results.length - 1][0].transcript.trim();
      setTranscript(newTranscript);
    };
    recognitionRef.current = recognition;
  }, [lang]); 

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      recognitionRef.current.start();
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  return { isListening, transcript, startListening, stopListening };
};
// ==================================================================
// --- Fin de los mini-hooks ---
// ==================================================================


function LSpacyHub() {
  const [hasConversationStarted, setHasConversationStarted] = useState(false);
  const [spacyState, setSpacyState] = useState('normal');
  const [currentText, setCurrentText] = useState("");
  
  const { speak, cancel, speaking } = useBrowserTTS();

  // Precargar GIFs
  useEffect(() => {
    const gifUrlsToPreload = [SpacyNormal, SpacyHablando, SpacyPensando, SpacyEscuchando, SpacyError];
    gifUrlsToPreload.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }, []);

  const say = useCallback((text) => {
    if (text) {
      setCurrentText(text);
      speak({ text: text });
    }
  }, [speak]);

  // Sincronizar estado del GIF con el habla
  useEffect(() => {
    if (speaking) {
      setSpacyState('hablando');
    } else if (!speaking && spacyState === 'hablando') {
      const timerId = setTimeout(() => {
        setSpacyState('normal');
      }, 500);
      return () => clearTimeout(timerId);
    }
  }, [speaking, spacyState]);

  // Manejo de clics en botones (Embudo Cero-Tokens)
  const handleFunnelClick = (type) => {
    cancel();
    setSpacyState('pensando');
    setCurrentText("");
    
    // Simulamos un pequeño "retraso de red" de 500ms para que se sienta como IA real
    setTimeout(() => {
      let responseText = "";
      if (type === "unique") {
        responseText = "Nuestra inteligencia artificial se adapta a ti en tiempo real. Aprenderás hablando con figuras históricas como Einstein y superando retos interactivos que garantizan tu fluidez en tiempo récord.";
      } else if (type === "price") {
        responseText = `Tenemos opciones para cada meta. Puedes iniciar ahora mismo con tu prueba gratis de ${duracionDeLaPrueba()}, o explorar nuestros planes Premium desde 400 pesos al mes para tener acceso ilimitado a Spacy.`;
      } else if (type === "easy") {
        responseText = "¡Es súper fácil! Sólo ve al final de la página, dale clic a iniciar prueba gratis, regístrate en menos de un minuto y prepárate para transformar tu inglés hoy mismo.";
      }
      
      setSpacyState('hablando');
      say(responseText);
    }, 600);
  };

  const handleStartConversation = () => {
    if (hasConversationStarted) return;
    setHasConversationStarted(true);
    const welcomeMessage = "¡Hola! Soy Spacy, tu asesor personal. Estoy aquí para mostrarte el futuro del aprendizaje de idiomas. ¿Qué te gustaría saber?";
    
    setTimeout(() => {
      setSpacyState('hablando');
      say(welcomeMessage);
    }, 500);
  };

  const getCurrentGif = () => {
    switch (spacyState) {
      case 'hablando': return SpacyHablando;
      case 'pensando': return SpacyPensando;
      case 'normal': default: return SpacyNormal;
    }
  };

  return (
    <section className="spacy-hub-section" id="spacy-hub">
      <div className="lp-container">
        <h2 className="section-title">Conoce a <span className="highlight-accent">Spacy</span></h2>
        <p className="section-subtitle">Tu asesor inteligente interactivo (Ahorro 100% de Tokens)</p>
        
        <div className="lsh-main-card">
          <div className="spacy-hero">
            <div className="spacy-avatar-container">
              <svg className="progress-ring" viewBox='0 0 400 400'>
                <circle className="progress-ring-bg" cx="200" cy="200" r="190" />
                <circle 
                  className={`progress-ring-bar ${spacyState === 'pensando' ? 'loading' : ''}`} 
                  cx="200" cy="200" r="190" 
                />
              </svg>

              <div className="spacy-avatar-main">
                {/* `key` obliga al navegador a recargar al cambiar de estado.
                    `muted` y `playsInline` no son opcionales: sin los dos, el
                    movil no deja que un video arranque solo. */}
                <video
                  key={getCurrentGif()}
                  src={getCurrentGif()}
                  className="spacy-img"
                  autoPlay
                  loop
                  muted
                  playsInline
                  aria-label="Spacy AI"
                />
                <div className="spacy-glow-effect"></div>
              </div>
            </div>
          </div> 

          <div className="spacy-controls-wrapper">
            
            {/* Pantalla de texto de respuesta */}
            {hasConversationStarted && currentText && (
              <div className="spacy-response-bubble" style={{ background: 'rgba(255,255,255,0.05)', padding: '15px 20px', borderRadius: '15px', marginBottom: '20px', borderLeft: '4px solid var(--accent)', color: 'var(--text-primary)', fontSize: '1.1rem', fontStyle: 'italic' }}>
                "{currentText}"
              </div>
            )}

            <div 
              className="spacy-interactive-funnel" 
              style={{ 
                display: 'flex', flexDirection: 'column', gap: '12px',
                opacity: hasConversationStarted ? 1 : 0, 
                pointerEvents: hasConversationStarted ? 'auto' : 'none' 
              }}
            >
              <button 
                className="hero-cta" 
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.2)', width: '100%', textAlign: 'left', padding: '16px 24px', fontSize: '1.1rem' }} 
                onClick={() => handleFunnelClick('unique')}
              >
                🚀 ¿Qué hace única a esta plataforma?
              </button>
              <button 
                className="hero-cta" 
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.2)', width: '100%', textAlign: 'left', padding: '16px 24px', fontSize: '1.1rem' }} 
                onClick={() => handleFunnelClick('price')}
              >
                💎 ¿Cuáles son los precios y planes?
              </button>
              <button 
                className="hero-cta" 
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.2)', width: '100%', textAlign: 'left', padding: '16px 24px', fontSize: '1.1rem' }} 
                onClick={() => handleFunnelClick('easy')}
              >
                ⚡ ¿Es difícil empezar a aprender?
              </button>
            </div>

            <div className="spacy-status" style={{ opacity: hasConversationStarted ? 1 : 0, pointerEvents: hasConversationStarted ? 'auto' : 'none', marginTop: '20px' }}>
              <span className={`status-dot ${spacyState === 'pensando' ? 'loading' : ''}`}></span>
              {spacyState === 'pensando' ? "Spacy está procesando..." : "Haz clic en una opción para escuchar"}
            </div>

            {!hasConversationStarted && (
              <div className="spacy-overlay-card">
                <p className="spacy-overlay-text">¡Hola! Soy Spacy, tu asesor personal. Estoy aquí para resolver tus dudas.</p>
                <button 
                  className="spacy-start-button-overlay"
                  onClick={handleStartConversation}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                  Conversar con Spacy
                </button>
              </div>
            )}
          </div> 
        </div> 
      </div> 
    </section>
  );
}

export default LSpacyHub;